import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RichTextNode } from '@enview/interface/types/actions/UserAction';
import base, { ApiTag } from './base';
import CustomField, {
  BillCustomField,
  CustomFieldType,
} from '@enview/interface/types/CustomFields';
import { State } from '../dux/@types';
import { updateCachedBillLists } from './bill';
import featureFlags, { FrontendFeature } from '../featureFlags';

interface CustomFieldParams {
  orgId: number;
  title: string;
  description?: string;
  type?: CustomFieldType;
  isPublic?: boolean;
}

export type BillFieldValueResponse = {
  billFieldValue: Partial<BillCustomField>;
  teamsAffected: number[];
};

export interface BillFieldValue {
  teams: number[];
  value: { richText: RichTextNode };
  orgId: number;
  shouldArchive?: boolean;
  valueId?: number;
}

const customFieldsAPI = base.injectEndpoints({
  endpoints: (build) => ({
    getOrganizationCustomFields: build.query<CustomField[], number>({
      query: (orgId) => ({
        url: `/v2/custom-fields/${orgId}`,
        method: 'GET',
      }),
      providesTags: [{ type: ApiTag.ORGANIZATION_CUSTOM_FIELD }],
    }),
    createCustomField: build.mutation<CustomField, CustomFieldParams>({
      query: (data) => {
        const { orgId, title, description, type, isPublic } = data;
        return {
          url: `/v2/custom-fields/${orgId}`,
          method: 'POST',
          data: {
            title,
            description,
            type,
            isPublic,
          },
        };
      },
      invalidatesTags: [ApiTag.ORGANIZATION_CUSTOM_FIELD],
    }),
    updateCustomField: build.mutation<
      CustomField,
      Partial<CustomFieldParams> & { id: number }
    >({
      query: (data) => {
        const { orgId, title, description, type, isPublic, id } = data;
        return {
          url: `/v2/custom-fields/${orgId}/${id}`,
          method: 'PATCH',
          data: {
            title,
            description,
            type,
            isPublic,
          },
        };
      },
      invalidatesTags: [ApiTag.ORGANIZATION_CUSTOM_FIELD],
    }),
    deleteCustomField: build.mutation<boolean, { id: number; orgId: number }>({
      query: ({ id, orgId }) => ({
        url: `/v2/custom-fields/${orgId}/${id}/archive`,
        method: 'POST',
      }),
      invalidatesTags: [ApiTag.ORGANIZATION_CUSTOM_FIELD],
    }),
    upsertCustomFieldValue: build.mutation<
      BillFieldValueResponse,
      { fieldId: number; billId: string; data: BillFieldValue }
    >({
      query: ({ fieldId, billId, data }) => ({
        url: `/v2/custom-fields/values/${fieldId}/bills/${billId}`,
        method: 'PUT',
        data,
      }),
      async onQueryStarted(
        { fieldId, billId, data },
        { getState, dispatch, queryFulfilled },
      ) {
        try {
          const { data: result } = await queryFulfilled;
          const { billFieldValue, teamsAffected } = result;
          const { orgId, shouldArchive } = data;

          const state = getState() as unknown as State;
          const { data: orgCustomFields } =
            customFieldsAPI.endpoints.getOrganizationCustomFields.select(orgId)(
              state as any,
            );
          const fieldTitle = orgCustomFields?.find((f) => f.id === fieldId)?.title;

          if (!fieldTitle) return;

          updateCachedBillLists(state, dispatch, (bill) => {
            if (bill.id !== billId) return;
            if (!bill.customFields) bill.customFields = {};
            if (!bill.customFields[orgId]) bill.customFields[orgId] = {};

            const orgFields = bill.customFields[orgId];

            if (shouldArchive) {
              teamsAffected.forEach((teamId) => {
                delete orgFields[teamId]?.[fieldTitle];
              });
            } else {
              const teamsAffectedSet = new Set(teamsAffected);
              Object.entries(orgFields).forEach(([teamIdStr, fields]) => {
                const teamId = Number(teamIdStr);
                if (
                  !teamsAffectedSet.has(teamId) &&
                  fields[fieldTitle]?.id === billFieldValue.id
                ) {
                  delete fields[fieldTitle];
                }
              });

              teamsAffected.forEach((teamId) => {
                if (!orgFields[teamId]) orgFields[teamId] = {};
                orgFields[teamId][fieldTitle] = billFieldValue;
              });
            }
          });
        } catch {
          // mutation failed
        }
      },
    }),
  }),
  overrideExisting: false,
});

// System custom fields (Workflow Status, Assigned, Priority) are hidden from the
// generic custom-field list (column config, the CustomFieldFilter dropdown, etc.)
// except for civic-eagle-staff users during the bill triage epic's staged rollout —
// same gate as the rest of that epic's frontend — or in dev, for testing.
export const useOrganizationCustomFields = (
  orgId: Parameters<
    typeof customFieldsAPI.endpoints.getOrganizationCustomFields.useQuery
  >[0],
) => {
  const result = customFieldsAPI.endpoints.getOrganizationCustomFields.useQuery(orgId);
  const organizationUser = useSelector(
    (state: State) => state.account.organizationUser,
  );
  const isSystemCustomFieldsEnabled =
    featureFlags.isFeatureEnabledForUser(
      FrontendFeature.SystemCustomFields,
      organizationUser,
    ) ||
    featureFlags.isFeatureEnabledForUser(
      FrontendFeature.BillTriageWorkspaceColumns,
      organizationUser,
    );

  const data = useMemo(
    () =>
      result.data?.filter((field) => !field.isSystem || isSystemCustomFieldsEnabled),
    [result.data, isSystemCustomFieldsEnabled],
  );

  return { ...result, data };
};

export default customFieldsAPI;
